import { BackBar } from '../../../components/BackBar';
import { TextField } from '../fields/TextField';
import { JoinBattleCheckbox } from '../fields/JoinBattleCheckbox';
import { ShirtSizePicker } from '../fields/ShirtSizePicker';
import { StepNav } from '../fields/StepNav';
import { isValidEmail, isValidPhone, canContinueChild } from '../lib';
import type { DraftChild, ParentContact } from '../lib';

interface ChildStepProps {
  draft: DraftChild;
  onChange: (d: DraftChild) => void;
  parent: ParentContact;
  onParentChange: (p: ParentContact) => void;
  parentLocked: boolean;
  submitting?: boolean;
  onBack: () => void;
  onContinue: () => void;
}

export function ChildStep({
  draft,
  onChange,
  parent,
  onParentChange,
  parentLocked,
  submitting = false,
  onBack,
  onContinue,
}: ChildStepProps) {
  const emailTrimmed = parent.parent_email.trim();
  const emailValid = isValidEmail(emailTrimmed);
  const emailShowError = emailTrimmed.length > 0 && !emailValid;
  const phoneTrimmed = parent.parent_phone.trim();
  const phoneValid = isValidPhone(phoneTrimmed);
  const phoneShowError = phoneTrimmed.length > 0 && !phoneValid;
  const canContinue = canContinueChild(draft, parent);

  return (
    <div className="smash-stage">
      <BackBar onBack={onBack} />
      <div className="smash-banner-wrap">
        <div className="smash-banner">
          <img src="/burst.png" alt="" aria-hidden="true" className="smash-burst" />
          <h1 className="smash-banner__title">A New Challenger Approaches…</h1>
        </div>
        <div className="smash-silhouette smash-silhouette--question" aria-hidden="true">?</div>
      </div>
      <form
        className="smash-panel"
        onSubmit={(e) => {
          e.preventDefault();
          if (canContinue) onContinue();
        }}
      >
        <h2 className="smash-panel__heading">Who is coming?</h2>

        <TextField
          label="Child's name"
          value={draft.child_name}
          onChange={(v) => onChange({ ...draft, child_name: v })}
          required
          maxLength={80}
        />

        <TextField
          label="Guardian name"
          value={parent.parent_name}
          onChange={(v) => onParentChange({ ...parent, parent_name: v })}
          required
          maxLength={120}
          readOnly={parentLocked}
        />

        <TextField
          label="Guardian email"
          value={parent.parent_email}
          onChange={(v) => onParentChange({ ...parent, parent_email: v })}
          required
          maxLength={254}
          readOnly={parentLocked}
          type="email"
          inputMode="email"
          autoComplete="email"
          error={emailShowError ? 'That doesn\'t look like a valid email (e.g. name@example.com).' : null}
          ariaInvalid={emailShowError}
        />

        <TextField
          label="Guardian phone"
          value={parent.parent_phone}
          onChange={(v) => onParentChange({ ...parent, parent_phone: v })}
          required
          maxLength={40}
          readOnly={parentLocked}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          error={phoneShowError ? 'Please enter a valid phone number.' : null}
          ariaInvalid={phoneShowError}
        />

        <JoinBattleCheckbox
          checked={draft.status === 'coming'}
          onChange={(checked) =>
            onChange({
              ...draft,
              status: checked ? 'coming' : 'not_coming',
              character_id: checked ? draft.character_id : null,
              shirt_size: checked ? draft.shirt_size : null,
            })
          }
        />

        {draft.status === 'coming' ? (
          <ShirtSizePicker value={draft.shirt_size} onChange={(s) => onChange({ ...draft, shirt_size: s })} />
        ) : (
          <p className="smash-regrets">Sorry, we'll miss you!</p>
        )}

        <StepNav
          primaryLabel={draft.status === 'coming' ? 'Choose Your Fighter' : 'Send my regrets'}
          primaryDisabled={!canContinue || submitting}
          onPrimary={onContinue}
          primaryType="submit"
          primaryArrow={draft.status === 'coming'}
        />
      </form>
    </div>
  );
}
